import React from 'react'
import { Button, Typography } from '@grammarly/badger-ui'
import { FormattedMessage, useIntl } from 'react-intl'

import { navHelper } from '../../../../../lib/services/navigation'
import { IconWarning } from '../../../../ui-lib/icons/warning'
import { IconClose } from '../../../../ui-lib/icons/close'
import { SpinnerIcon } from '../../../../ui-lib/icons/spinner'
import { ButtonCta } from '../../../../ui-lib/button'
import { FixedSpacer } from '../../../../ui-lib/spacer'

import { UploadButton } from '../upload-button'

import { TextCheckStatus } from '../types'
import styles from './overlay.module.scss'

const { C2, C3 } = Typography

interface TextCheckerOverlayProps {
  status: TextCheckStatus
  setStatus: (status: TextCheckStatus) => void
  setText: (text: string) => void
  onClose: () => void
  scanForPlagiarism?: boolean
}

const MainText: React.FC<{ children?: React.ReactNode }> = (props) => (
  <C2 as="div" bold>
    {props.children}
  </C2>
)

const SubText: React.FC<{ children?: React.ReactNode }> = (props) => (
  <C3 as="div" className={styles.subText}>
    {props.children}
  </C3>
)

const NoAlertsCTA: React.FC = () => {
  //TODO: add tracking
  return (
    <ButtonCta
      onClick={() => navHelper.navigateFunnelPath('/plans')}
      text={
        <FormattedMessage
          id="global.uiAction.learnMore"
          description="Text for CTA that prompts user to learn more by following a link or taking some other action"
          defaultMessage="Learn More"
        />
      }
    />
  )
}

const overlayLoadingText = {
  plagiarism: {
    mainText: (
      <MainText>
        <FormattedMessage
          id="textChecker.overlayLoadingText.plagiarism.mainText"
          description="Text Checker - Overlay Loading Text - Plagiarism - Main Text"
          defaultMessage="Scan for plagiarism"
        />
      </MainText>
    ),
    subText: (
      <SubText>
        <FormattedMessage
          id="textChecker.overlayLoadingText.plagiarism.subText"
          description="Text Checker - Overlay Loading Text - Plagiarism - Sub Text"
          defaultMessage="Grammarly is looking for plagiarism and writing mistakes."
        />
      </SubText>
    ),
  },
  default: {
    subText: (
      <SubText>
        <FormattedMessage
          id="textChecker.overlayLoadingText.default.subText"
          description="Text Checker - Overlay Loading Text - Default - Sub Text"
          defaultMessage="Grammarly is proofreading your text"
        />
      </SubText>
    ),
  },
}

export const TextCheckerOverlay = ({
  status,
  onClose,
  setStatus,
  setText,
  scanForPlagiarism,
}: TextCheckerOverlayProps) => {
  const intl = useIntl()
  const showCloseButton = status !== 'LOADING_TEXT_CHECK' && status !== 'LOADING_FILE'

  return (
    <div className={styles.container}>
      {showCloseButton && (
        <Button
          className={styles.closeButton}
          onClick={onClose}
          padding="none"
          aria-label={intl.formatMessage({
            id: 'global.uiAction.close',
            description: 'Text for CTA that closes an element',
            defaultMessage: 'Close',
          })}
        >
          <IconClose aria-hidden="true" />
        </Button>
      )}
      <div className={styles.contentContainer}>
        {status === 'LOADING_TEXT_CHECK' && (
          <>
            <SpinnerIcon />
            {scanForPlagiarism ? overlayLoadingText.plagiarism.mainText : null}
            {scanForPlagiarism ? overlayLoadingText.plagiarism.subText : overlayLoadingText.default.subText}
          </>
        )}
        {status === 'LOADING_FILE' && <SpinnerIcon />}
        {status === 'NO_ALERTS_FOUND' && (
          <>
            <MainText>
              <FormattedMessage
                id="textChecker.overlay.status.noAlertsFound.mainText"
                description="Text Checker - Overlay - Status - No Alerts Found - Main Text"
                defaultMessage="Nice Work!"
              />
            </MainText>
            <FixedSpacer multiplier={1} />
            <SubText>
              <FormattedMessage
                id="textChecker.overlay.status.noAlertsFound.subText"
                description="Text Checker - Overlay - Status - No Alerts Found - Sub Text"
                defaultMessage="Grammarly found no errors in your text. Learn more about how Grammarly can help improve your writing."
              />
            </SubText>
            <FixedSpacer multiplier={2} />
            <NoAlertsCTA />
          </>
        )}
        {status === 'UNKNOWN_ERROR' && (
          <>
            <MainText>
              <FormattedMessage
                id="textChecker.overlay.status.unknownError.mainText"
                description="Text Checker - Overlay - Status - Unknown Error - Main Text"
                defaultMessage="oops!"
              />
            </MainText>
            <FixedSpacer multiplier={1} />
            <SubText>
              <FormattedMessage
                id="textChecker.overlay.status.unknownError.subText"
                description="Text Checker - Overlay - Status - Unknown Error - Sub Text"
                defaultMessage="We are having trouble checking your text right now. Learn more about how Grammarly can help improve your writing."
              />
            </SubText>
            <FixedSpacer multiplier={2} />
            <NoAlertsCTA />
          </>
        )}
        {(status === 'TOO_MUCH_TEXT' || status === 'FILE_ERROR_TOO_BIG') && (
          <>
            <IconWarning />
            <MainText>
              <FormattedMessage
                id="textChecker.overlay.status.tooMuchTextOrFileErrorTooBig.mainText"
                description="Text Checker - Overlay - Status - Too Much Text or File Error Too Big - Main Text"
                defaultMessage="Too much text!"
              />
            </MainText>
            <SubText>
              <FormattedMessage
                id="textChecker.overlay.status.tooMuchTextOrFileErrorTooBig.subText"
                description="Text Checker - Overlay - Status - Too Much Text or File Error Too Big - Sub Text"
                defaultMessage="Try checking less than 20 pages at a time."
              />
            </SubText>
            <UploadButton setStatus={setStatus} setText={setText} />
          </>
        )}
        {status === 'FILE_ERROR_INVALID_EXT' && (
          <>
            <IconWarning />
            <MainText>
              <FormattedMessage
                id="textChecker.overlay.status.fileErrorInvalidExtension.mainText"
                description="Text Checker - Overlay - Status - File Error Invalid Extension - Main Text"
                defaultMessage="This file type is unsupported."
              />
            </MainText>
            <SubText>
              <FormattedMessage
                id="textChecker.overlay.status.fileErrorInvalidExtension.subText"
                description="Text Checker - Overlay - Status - File Error Invalid Extension - Sub Text"
                defaultMessage="Supported types: .doc, .docx, .odt, .txt and .rtf"
              />
            </SubText>
            <UploadButton setStatus={setStatus} setText={setText} />
          </>
        )}
      </div>
    </div>
  )
}
